import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Barcode, Package, ChevronLeft, ChevronRight, ClipboardList, Store } from 'lucide-react';
import { productosService, categoriasService } from '@/services/productos.service';
import { sucursalesService } from '@/services/sucursales.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { ImportExcelModal } from './import-excel-modal';
import { FileSpreadsheet } from 'lucide-react';

export function ProductsListPage() {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [branches, setBranches] = useState<any[]>([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { handleApiError, handleAuthRedirect } = useApiError();
  const limit = 20;

  useEffect(() => {
    if (user?.company?.id) {
      categoriasService.findAll(user.company.id).then(setCategories).catch(() => {});
      sucursalesService.findByCompany(user.company.id).then(setBranches).catch(() => {});
    }
  }, [user?.company?.id]);

  useEffect(() => {
    loadProducts();
  }, [search, page, categoryFilter]);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await productosService.findAll({
        companyId: user?.company?.id,
        search: search || undefined,
        categoryId: categoryFilter || undefined,
        page,
        limit,
      });
      setProducts(response.data || []);
      setTotal(response.total || 0);
      setTotalPages(response.totalPages || 1);
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al cargar productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Calculate total stock considering branch filter
  const getProductStock = (p: any) => {
    if (branchFilter && p.branchProducts) {
      const bp = p.branchProducts.find((b: any) => b.branchId === branchFilter);
      return bp ? bp.stock : 0;
    }
    return p.stock; // global stock
  };

  const lowStockCount = products.filter((p: any) => getProductStock(p) < p.minStock).length;
  const outOfStockCount = products.filter((p: any) => getProductStock(p) === 0).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Productos</h1>
          <p className="text-sm text-ios-gray mt-1">Gestiona tu catálogo de productos</p>
        </div>
        <div className="flex items-center gap-2">
          {['SUPER_ADMIN', 'ADMIN', 'GERENTE', 'GERENTE_GENERAL'].includes(user?.role || '') && (
            <button onClick={() => setShowImportModal(true)} className="ios-button bg-ios-gray-6 text-black hover:bg-ios-gray-5 flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-ios-green" />
              Importar Excel
            </button>
          )}
          <button onClick={() => navigate('/products/new')} className="ios-button flex items-center gap-2">
            <Plus size={18} />
            Nuevo Producto
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ios-gray" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Buscar por nombre, SKU o código de barras..."
            className="ios-input w-full pl-10"
          />
        </div>
        <select
          className="ios-input w-48"
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
        >
          <option value="">Todas las categorías</option>
          {categories.map((cat: any) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        <select
          className="ios-input w-48"
          value={branchFilter}
          onChange={(e) => { setBranchFilter(e.target.value); setPage(1); }}
        >
          <option value="">Todas las Sucursales</option>
          {branches.map((b: any) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="flex gap-4 text-sm">
          <span className="text-ios-gray">
            <strong className="text-foreground">{total}</strong> productos activos
          </span>
          {lowStockCount > 0 && (
            <span className="text-ios-gray">
              <strong className="text-ios-orange">{lowStockCount}</strong> con stock bajo
            </span>
          )}
          {outOfStockCount > 0 && (
            <span className="text-ios-gray">
              <strong className="text-ios-red">{outOfStockCount}</strong> agotados
            </span>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-3 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Package size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">No hay productos</h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'No se encontraron productos con ese término' : 'Comienza agregando tu primer producto'}
          </p>
          {!search && (
            <button onClick={() => navigate('/products/new')} className="ios-button">
              + Nuevo Producto
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Products table */}
          <div className="ios-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">SKU</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Categoría</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Marca</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Clave SAT</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Precio</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Stock</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-ios-gray uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {products.map((product: any) => {
                  const currentStock = getProductStock(product);
                  const isLowStock = currentStock < product.minStock;
                  const isOutOfStock = currentStock === 0;
                  return (
                    <tr key={product.id} className="hover:bg-ios-gray-6/30 transition-colors cursor-pointer" onClick={() => navigate(`/products/${product.id}`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-ios-gray-6 rounded-[10px] flex items-center justify-center">
                            <Package size={18} className="text-ios-gray" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{product.name}</p>
                            {product.barcode && (
                              <p className="text-xs text-ios-gray flex items-center gap-1">
                                <Barcode size={10} /> {product.barcode}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-ios-gray">{product.sku}</td>
                      <td className="px-4 py-3 text-sm">{product.category?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm">{product.brand?.name || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        {product.satKey ? (
                          <code className="text-xs font-mono bg-ios-blue/10 text-ios-blue px-2 py-0.5 rounded-md">{product.satKey}</code>
                        ) : (
                          <span className="text-xs text-ios-gray">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">${Number(product.pricePublic).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-medium ${isOutOfStock ? 'text-ios-red' : isLowStock ? 'text-ios-orange' : 'text-ios-green'}`}>
                          {isOutOfStock ? 'Agotado' : currentStock}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/kardex/${product.id}`); }} className="text-xs text-ios-gray hover:text-ios-blue hover:underline font-medium flex items-center gap-1" title="Ver Kardex">
                            <ClipboardList size={12} /> Kardex
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/products/${product.id}/edit`); }} className="text-xs text-ios-blue hover:underline font-medium">Editar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-ios-gray">Página {page} de {totalPages} ({total} productos)</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="ios-input px-3 py-2 disabled:opacity-50">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  if (p > totalPages) return null;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`ios-input px-3 py-2 ${page === p ? 'bg-ios-blue text-white border-ios-blue' : ''}`}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="ios-input px-3 py-2 disabled:opacity-50">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showImportModal && (
        <ImportExcelModal 
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadProducts();
          }}
        />
      )}
    </div>
  );
}
